import { useEffect, useMemo, useRef, useState } from 'react'
import type { Media } from '@nhanho/shared'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import {
  clampOffset,
  cropToView,
  defaultCrop,
  fitCenter,
  minScale,
  rotatedSize,
  viewToCrop,
  type CropValue,
  type ViewState,
} from '../../lib/crop'

export type { CropValue } from '../../lib/crop'
export { defaultCrop } from '../../lib/crop'

/**
 * Trình cắt ảnh **vuông** cho dải "Cảm hứng từ Nhà Nhỏ" (2026-09-23).
 *
 * Kéo để di chuyển · lăn chuột hoặc thanh trượt để phóng to · hai nút xoay 90°.
 * Xoay tự do theo độ đã bị loại — xem `docs/DECISIONS.md` [2026-09-23].
 *
 * **Không dùng thư viện cắt ảnh** (CLAUDE.md §7): admin đã tự viết rich-text, icon, dropzone;
 * phần khó ở đây không phải kéo–thả mà là phép đổi toạ độ, mà phép đó tự viết thì kiểm được.
 *
 * ⚠️ Cắt **chỉ là thông số**. Ảnh vuông thật do `apps/api` sinh bằng sharp lúc lưu
 * (`lib/images.ts` → `cropSquare`). Ở đây tuyệt đối không đụng canvas: cắt hai nơi bằng hai
 * công cụ khác nhau là hai kết quả khác nhau cho cùng một khung.
 */

/** Cạnh khung xem, px. Cố định để phép đổi toạ độ không phụ thuộc bề ngang cửa sổ. */
const VIEW = 420

/**
 * Khung cắt nhỏ nhất cho phép, px trên ảnh gốc. Khớp `size >= 200` của Zod ở
 * `api/src/routes/settings.ts` — hai nơi phải bằng nhau.
 */
const MIN_CROP = 200

/**
 * Trần phóng to = mức làm khung cắt vừa chạm `MIN_CROP`.
 *
 * ⚠️ Trước đây là hằng `8` và đo 23/09 mới thấy hỏng: với ảnh 3024px, `scale = 8` cho khung
 * **52px** — dưới ngưỡng tối thiểu từ lâu, nên **hơn 3/4 thanh trượt là vùng chết**, kéo vào đó
 * chỉ làm nút "Dùng khung này" tắt mà không nói vì sao. Buộc trần theo đúng ngưỡng thì cả thanh
 * trượt đều dùng được và không bao giờ trượt ra ngoài mức API nhận.
 */
const maxScaleFor = (min: number) => Math.max(min, VIEW / MIN_CROP)

export function SquareCropper({
  open,
  media,
  value,
  onClose,
  onSave,
}: {
  open: boolean
  media: Media | null
  value: CropValue | null
  onClose: () => void
  onSave: (crop: CropValue) => void
}) {
  const [view, setView] = useState<ViewState | null>(null)
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null)

  /**
   * ⚠️ `value` đi qua **ref**, không qua deps. Cùng bài học với `useList(fetcher, defaults)`
   * (`CLAUDE.md` §7): caller dựng object mới ở mỗi lần render, để nó vào deps là effect chạy
   * lại giữa lúc người dùng đang kéo — khung nhảy về vị trí ban đầu, không rõ vì sao.
   */
  const valueRef = useRef(value)
  valueRef.current = value

  /** Nạp khung mỗi lần **mở** (hoặc đổi ảnh): đã lưu thì mở đúng khung cũ, chưa thì ô giữa ảnh. */
  useEffect(() => {
    if (!open || !media) return
    setView(cropToView(valueRef.current ?? defaultCrop(media.width, media.height), VIEW))
  }, [open, media?.id])

  const bounds = useMemo(() => {
    if (!media || !view) return null
    const min = minScale(VIEW, media.width, media.height, view.rotate)
    return { ...rotatedSize(media.width, media.height, view.rotate), min, max: maxScaleFor(min) }
  }, [media, view?.rotate])

  if (!media) return null

  /** Mọi thay đổi đi qua đây để không bao giờ thoát khỏi biên — kể cả khi đổi mức phóng. */
  const apply = (next: ViewState) => {
    if (!bounds) return
    const scale = Math.max(bounds.min, Math.min(bounds.max, next.scale))
    setView({
      rotate: next.rotate,
      scale,
      offsetX: clampOffset(next.offsetX, VIEW, bounds.w, scale),
      offsetY: clampOffset(next.offsetY, VIEW, bounds.h, scale),
    })
  }

  /**
   * Phóng to **quanh tâm khung xem**, không quanh góc trái–trên: phóng quanh góc thì ảnh trôi
   * ra khỏi tầm nhìn ngay lần lăn chuột đầu tiên và người dùng phải kéo lại từ đầu.
   */
  const zoomTo = (scale: number) => {
    if (!view || !bounds) return
    const s = Math.max(bounds.min, Math.min(bounds.max, scale))
    const k = s / view.scale
    apply({
      ...view,
      scale: s,
      offsetX: VIEW / 2 - (VIEW / 2 - view.offsetX) * k,
      offsetY: VIEW / 2 - (VIEW / 2 - view.offsetY) * k,
    })
  }

  /**
   * Xoay xong **đặt lại khung về giữa**. Giữ nguyên offset thì với ảnh không vuông, khung đang
   * ở mép phải sau khi xoay 90° sẽ rơi ra ngoài ảnh — kẹp lại được, nhưng người dùng thấy ảnh
   * nhảy một cái không rõ vì sao. Về giữa là hành vi đoán được.
   */
  const rotateBy = (delta: 90 | -90) => {
    if (!view) return
    const rotate = ((((view.rotate + delta) % 360) + 360) % 360) as 0 | 90 | 180 | 270
    setView(fitCenter(VIEW, media.width, media.height, rotate))
  }

  const reset = () => setView(fitCenter(VIEW, media.width, media.height, 0))

  const cropNow = view ? viewToCrop(view, VIEW) : null
  /** Cạnh ảnh sẽ sinh ra. Dưới 400px thì ô 190px trên màn retina bắt đầu thấy mờ. */
  const outSide = cropNow ? Math.min(800, cropNow.size) : 0

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Cắt khung vuông — ${media.alt}`}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Huỷ</Button>
          {/* Chặn ở đây thay vì để API trả 422: `size >= 200` là luật của `routes/settings.ts`,
              người dùng không nên biết về nó qua một thông báo lỗi sau khi bấm Lưu cả trang. */}
          <Button onClick={() => cropNow && onSave(cropNow)} disabled={!cropNow || cropNow.size < MIN_CROP}>
            Dùng khung này
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-4">
        <div
          className="relative touch-none overflow-hidden rounded-image border border-border bg-sand"
          style={{ width: VIEW, height: VIEW }}
          onPointerDown={(e) => {
            if (!view) return
            e.currentTarget.setPointerCapture(e.pointerId)
            drag.current = { x: e.clientX, y: e.clientY, ox: view.offsetX, oy: view.offsetY }
          }}
          onPointerMove={(e) => {
            const d = drag.current
            if (!d || !view) return
            apply({ ...view, offsetX: d.ox + (e.clientX - d.x), offsetY: d.oy + (e.clientY - d.y) })
          }}
          onPointerUp={() => (drag.current = null)}
          onPointerCancel={() => (drag.current = null)}
          onWheel={(e) => view && zoomTo(view.scale * (e.deltaY < 0 ? 1.1 : 1 / 1.1))}
        >
          {view && (
            <img
              src={media.url}
              alt=""
              draggable={false}
              className="absolute left-0 top-0 max-w-none origin-top-left select-none"
              /**
               * Thứ tự `translate → rotate → scale` là thứ tự phép đổi toạ độ ở `viewToCrop`
               * giả định. Đảo lại là khung tính ra một đằng, ảnh hiện một nẻo.
               *
               * `rotate` quanh tâm ảnh gốc rồi dịch về đúng chỗ: với 90°/270° hai cạnh đổi nhau
               * nên phải bù đúng nửa hiệu hai cạnh, nếu không ảnh lệch khỏi khung.
               */
              style={{
                width: media.width,
                height: media.height,
                transform: [
                  `translate(${view.offsetX}px, ${view.offsetY}px)`,
                  `scale(${view.scale})`,
                  view.rotate === 90
                    ? `translate(${media.height}px, 0) rotate(90deg)`
                    : view.rotate === 180
                      ? `translate(${media.width}px, ${media.height}px) rotate(180deg)`
                      : view.rotate === 270
                        ? `translate(0, ${media.width}px) rotate(270deg)`
                        : '',
                ].join(' '),
              }}
            />
          )}
          <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/70" />
        </div>

        <div className="flex w-full max-w-md flex-col gap-3">
          <label className="flex items-center gap-3 text-xs text-ink-muted">
            <span className="w-16 shrink-0">Phóng to</span>
            <input
              type="range"
              min={0}
              max={100}
              value={view && bounds && bounds.max > bounds.min ? Math.round(((view.scale - bounds.min) / (bounds.max - bounds.min)) * 100) : 0}
              onChange={(e) => bounds && zoomTo(bounds.min + (Number(e.target.value) / 100) * (bounds.max - bounds.min))}
              className="w-full"
            />
          </label>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => rotateBy(-90)}>↺ Xoay trái</Button>
              <Button variant="secondary" size="sm" onClick={() => rotateBy(90)}>↻ Xoay phải</Button>
              <Button variant="ghost" size="sm" onClick={reset}>Vừa khung</Button>
            </div>
            {cropNow && (
              <span className={`text-xs ${outSide < 400 ? 'text-danger' : 'text-ink-muted'}`}>
                Ảnh sẽ sinh ra: {outSide}×{outSide}
                {outSide < 400 && ' — nhỏ quá, sẽ mờ'}
              </span>
            )}
          </div>

          <p className="text-xs text-ink-muted">
            Kéo ảnh để chọn phần muốn giữ. Ô vuông này đúng bằng ô sẽ hiện trên trang chủ.
          </p>
        </div>
      </div>
    </Modal>
  )
}
